/**
 * AppContext.jsx
 *
 * Global state for Tayst Content Studio — Phase 2: API-backed.
 *
 * All data (ideas, contentPieces, conPosts, partners, settings, appUsers)
 * is loaded from the Laravel API on mount.  Every mutation calls the API
 * first and updates local state on success, keeping the UI fast and
 * consistent even on slow connections (optimistic where it makes sense).
 *
 * AI keys (Anthropic, OpenAI) stay client-side only — never sent to the
 * DB — until a backend proxy for AI calls is added.
 */

import React, {
  createContext, useContext, useReducer,
  useState, useEffect, useCallback,
} from 'react';

import { authApi }          from '../api/auth';
import { ideasApi }         from '../api/ideas';
import { contentApi }       from '../api/content';
import { consumerPostsApi } from '../api/consumerPosts';
import { partnersApi }      from '../api/partners';
import { settingsApi }      from '../api/settings';
import { usersApi }         from '../api/users';
import { activityLogApi }   from '../api/activityLog';

const AppContext = createContext(null);

// ─────────────────────────────────────────────────────────────
// Reducer — pure local state updates (no side effects)
// ─────────────────────────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {

    // ── Bootstrap (API data loaded on mount) ───────────────
    case 'BOOT':
      return { ...state, ...action.payload };

    // ── Ideas ──────────────────────────────────────────────
    case 'SET_IDEAS':
      return { ...state, ideas: action.ideas };
    case 'ADD_IDEA':
      return { ...state, ideas: [action.idea, ...state.ideas] };
    case 'UPDATE_IDEA':
      return { ...state, ideas: state.ideas.map(i => i.id === action.idea.id ? action.idea : i) };
    case 'REMOVE_IDEA':
      return { ...state, ideas: state.ideas.filter(i => i.id !== action.id) };

    // ── Content Pieces ─────────────────────────────────────
    case 'SET_CONTENT_PIECES':
      return { ...state, contentPieces: action.contentPieces };
    case 'ADD_CONTENT_PIECES':
      return { ...state, contentPieces: [...action.pieces, ...state.contentPieces] };
    case 'UPDATE_CONTENT_PIECE':
      return { ...state, contentPieces: state.contentPieces.map(p => p.id === action.piece.id ? action.piece : p) };
    case 'REMOVE_CONTENT_PIECE':
      return { ...state, contentPieces: state.contentPieces.filter(p => p.id !== action.id) };

    // ── Consumer Posts ─────────────────────────────────────
    case 'SET_CON_POSTS':
      return { ...state, conPosts: action.conPosts };
    case 'ADD_CON_POST':
      return { ...state, conPosts: [action.post, ...state.conPosts] };
    case 'UPDATE_CON_POST':
      return { ...state, conPosts: state.conPosts.map(p => p.id === action.post.id ? action.post : p) };
    case 'REMOVE_CON_POST':
      return { ...state, conPosts: state.conPosts.filter(p => p.id !== action.id) };

    // ── Partners ───────────────────────────────────────────
    case 'SET_PARTNERS':
      return { ...state, partners: action.partners };
    case 'ADD_PARTNER':
      return { ...state, partners: [...state.partners, action.partner] };
    case 'UPDATE_PARTNER':
      return { ...state, partners: state.partners.map(p => p.id === action.partner.id ? action.partner : p) };
    case 'REMOVE_PARTNER':
      return { ...state, partners: state.partners.filter(p => p.id !== action.id) };

    // ── Settings ───────────────────────────────────────────
    case 'SET_SETTINGS':
      return { ...state, settings: action.settings };
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.changes } };

    // ── Voice (stored as a settings key) ───────────────────
    case 'SET_VOICE':
      return { ...state, voiceState: action.voice };
    case 'UPDATE_VOICE':
      return { ...state, voiceState: { ...state.voiceState, ...action.voice } };

    // ── Users ──────────────────────────────────────────────
    case 'SET_USERS':
      return { ...state, appUsers: action.users };
    case 'ADD_USER':
      return { ...state, appUsers: [...state.appUsers, action.user] };
    case 'UPDATE_USER':
      return { ...state, appUsers: state.appUsers.map(u => u.id === action.user.id ? action.user : u) };
    case 'REMOVE_USER':
      return { ...state, appUsers: state.appUsers.filter(u => u.id !== action.id) };

    // ── Activity ───────────────────────────────────────────
    case 'LOG_ACTIVITY': {
      const activityLog = [action.entry, ...state.activityLog].slice(0, 200);
      return { ...state, activityLog };
    }

    // ── Scheduling (local mirror of DB state) ──────────────
    case 'SCHEDULE_PIECE': {
      const { pieceId, dateStr } = action;
      const existing = state.scheduled[dateStr] || [];
      if (existing.includes(pieceId)) return state;
      const scheduled = { ...state.scheduled, [dateStr]: [...existing, pieceId] };
      const contentPieces = state.contentPieces.map(p =>
        p.id === pieceId ? { ...p, scheduled_date: dateStr, status: 'scheduled' } : p
      );
      return { ...state, scheduled, contentPieces };
    }
    case 'UNSCHEDULE_PIECE': {
      const { pieceId, dateStr } = action;
      const ids = (state.scheduled[dateStr] || []).filter(id => id !== pieceId);
      const scheduled = { ...state.scheduled, [dateStr]: ids };
      const contentPieces = state.contentPieces.map(p =>
        p.id === pieceId ? { ...p, scheduled_date: null, status: 'approved' } : p
      );
      return { ...state, scheduled, contentPieces };
    }

    default:
      return state;
  }
}

// Build the initial scheduled map from loaded content pieces
function buildScheduled(pieces) {
  const map = {};
  for (const p of pieces) {
    if (p.scheduled_date) {
      const d = p.scheduled_date.slice(0, 10);
      map[d] = [...(map[d] || []), p.id];
    }
  }
  return map;
}

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────
export function AppProvider({ children }) {

  // ── Auth ─────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(() => authApi.storedUser());
  const [authLoading, setAuthLoading] = useState(false);

  // ── Data loading state ───────────────────────────────────
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError,   setDataError]   = useState(null);

  // ── Navigation state ─────────────────────────────────────
  const [activeWorld,    setActiveWorld]    = useState('biz');
  const [bizTab,         setBizTab]         = useState('ideas');
  const [conTab,         setConTab]         = useState('con-ideas');
  const [activeQueueId,  setActiveQueueId]  = useState(null);

  // ── AI keys — client-side only ───────────────────────────
  const [anthropicKey, setAnthropicKeyState] = useState(
    () => localStorage.getItem('tayst_anthropic_key') || ''
  );
  const [openaiKey, setOpenaiKeyState] = useState(
    () => localStorage.getItem('tayst_openai_key') || ''
  );

  // Ready = either the user picked "Server Key" (backend handles it)
  //         or a custom key has been saved in the browser.
  const anthropicKeyReady = !!anthropicKey || localStorage.getItem('tayst_anth_source') === 'env';
  const openaiKeyReady    = !!openaiKey    || localStorage.getItem('tayst_oai_source')  === 'env';

  // ── Data reducer ─────────────────────────────────────────
  const [state, dispatch] = useReducer(reducer, {
    ideas:        [],
    contentPieces:[],
    conPosts:     [],
    partners:     [],
    scheduled:    {},
    voiceState:   { description:'', pov:'', audience:'', proof_points:'', signature_phrase:'', examples:'', tones:[], banned:[] },
    settings:     { companyName:'Tayst Coffee', linkedinPage:'', websiteUrl:'', businessClients:200, hotelPartners:500 },
    activityLog:  [],
    appUsers:     [],
  });

  // ── Load all data from API once the user is authenticated ─
  useEffect(() => {
    if (!currentUser) return;
    loadAllData();
  }, [currentUser?.id]);   // re-load if the user changes (e.g. admin switch)

  async function loadAllData() {
    setDataLoading(true);
    setDataError(null);
    try {
      const [ideas, pieces, conPosts, partners, settings, users, activityLog] = await Promise.all([
        ideasApi.getAll(),
        contentApi.getAll(),
        consumerPostsApi.getAll(),
        partnersApi.getAll(),
        settingsApi.getAll(),
        currentUser?.role === 'admin' ? usersApi.getAll() : Promise.resolve([]),
        currentUser?.role === 'admin' ? activityLogApi.getAll() : Promise.resolve([]),
      ]);

      // The API stores settings as keyed blobs:
      //   voice_profile → object used for AI prompts
      //   brand         → flat brand settings (companyName, linkedinPage, etc.)
      // We spread `brand` into the flat settings state so components can read
      // settings.companyName directly.
      const { voice_profile, brand, ...otherSettings } = settings;
      const flatBrand = typeof brand === 'object' && brand !== null ? brand : {};

      dispatch({
        type: 'BOOT',
        payload: {
          ideas,
          contentPieces: pieces,
          conPosts,
          partners,
          settings:     { ...otherSettings, ...flatBrand },
          voiceState:   voice_profile || state.voiceState,
          scheduled:    buildScheduled(pieces),
          appUsers:     users,
          activityLog,
        },
      });
    } catch (err) {
      console.error('[AppContext] loadAllData failed:', err);
      setDataError(err.message || 'Failed to load data from API.');
    } finally {
      setDataLoading(false);
    }
  }

  // ── Auth actions ─────────────────────────────────────────
  async function login(email, password) {
    setAuthLoading(true);
    try {
      const { user } = await authApi.login(email, password);
      setCurrentUser(user);
      return user;
    } finally {
      setAuthLoading(false);
    }
  }

  async function logout() {
    await authApi.logout();
    setCurrentUser(null);
    dispatch({ type: 'BOOT', payload: { ideas:[], contentPieces:[], conPosts:[], partners:[], scheduled:{}, appUsers:[], activityLog:[] } });
  }

  // ── AI key helpers ───────────────────────────────────────
  function setAnthropicKey(key) {
    localStorage.setItem('tayst_anthropic_key', key);
    setAnthropicKeyState(key);
  }
  function setOpenaiKey(key) {
    localStorage.setItem('tayst_openai_key', key);
    setOpenaiKeyState(key);
  }

  // ── Activity log helper ──────────────────────────────────
  function logActivity(action, detail) {
    dispatch({
      type: 'LOG_ACTIVITY',
      entry: {
        id:        crypto.randomUUID(),
        userName:  currentUser?.name || 'System',
        initials:  currentUser?.avatar_initials || currentUser?.name?.slice(0,2).toUpperCase() || 'SY',
        action,
        detail:    detail || '',
        createdAt: new Date().toISOString(),
      },
    });
  }

  // ─────────────────────────────────────────────────────────
  // Async mutators — call API, then update local state
  // ─────────────────────────────────────────────────────────

  // IDEAS
  async function addIdea(payload) {
    const idea = await ideasApi.create(payload);
    dispatch({ type: 'ADD_IDEA', idea });
    logActivity('Submitted idea', idea.title);
    return idea;
  }

  async function updateIdea(id, changes) {
    const idea = await ideasApi.update(id, changes);
    dispatch({ type: 'UPDATE_IDEA', idea });
    return idea;
  }

  async function removeIdea(id) {
    await ideasApi.remove(id);
    dispatch({ type: 'REMOVE_IDEA', id });
    logActivity('Removed idea');
  }

  // CONTENT PIECES
  async function addContentPieces(payloads) {
    // Create all pieces in a single bulk request
    const pieces = await contentApi.createBulk(payloads);
    dispatch({ type: 'ADD_CONTENT_PIECES', pieces });
    return pieces;
  }

  async function updateContentPiece(id, changes) {
    const piece = await contentApi.update(id, changes);
    dispatch({ type: 'UPDATE_CONTENT_PIECE', piece });
    return piece;
  }

  async function removeContentPiece(id) {
    await contentApi.remove(id);
    dispatch({ type: 'REMOVE_CONTENT_PIECE', id });
  }

  async function schedulePiece(pieceId, dateStr) {
    // Optimistic update first for snappy UX
    dispatch({ type: 'SCHEDULE_PIECE', pieceId, dateStr });
    try {
      await contentApi.schedule(pieceId, dateStr);
    } catch (err) {
      // Revert on failure
      dispatch({ type: 'UNSCHEDULE_PIECE', pieceId, dateStr });
      throw err;
    }
  }

  async function unschedulePiece(pieceId, dateStr) {
    dispatch({ type: 'UNSCHEDULE_PIECE', pieceId, dateStr });
    try {
      await contentApi.unschedule(pieceId);
    } catch (err) {
      dispatch({ type: 'SCHEDULE_PIECE', pieceId, dateStr });
      throw err;
    }
  }

  // CONSUMER POSTS
  async function addConPost(payload) {
    const post = await consumerPostsApi.create(payload);
    dispatch({ type: 'ADD_CON_POST', post });
    return post;
  }

  async function updateConPost(id, changes) {
    const post = await consumerPostsApi.update(id, changes);
    dispatch({ type: 'UPDATE_CON_POST', post });
    return post;
  }

  async function removeConPost(id) {
    await consumerPostsApi.remove(id);
    dispatch({ type: 'REMOVE_CON_POST', id });
  }

  // PARTNERS
  async function addPartner(payload) {
    const partner = await partnersApi.create(payload);
    dispatch({ type: 'ADD_PARTNER', partner });
    logActivity('Added partner', partner.name);
    return partner;
  }

  async function updatePartner(id, changes) {
    const partner = await partnersApi.update(id, changes);
    dispatch({ type: 'UPDATE_PARTNER', partner });
    return partner;
  }

  async function removePartner(id) {
    await partnersApi.remove(id);
    dispatch({ type: 'REMOVE_PARTNER', id });
    logActivity('Removed partner');
  }

  // VOICE PROFILE (stored as settings key)
  async function updateVoice(voice) {
    dispatch({ type: 'UPDATE_VOICE', voice });  // optimistic
    try {
      await settingsApi.set('voice_profile', voice);
    } catch (err) {
      // Revert — re-fetch to be safe
      const fresh = await settingsApi.get('voice_profile');
      dispatch({ type: 'SET_VOICE', voice: fresh });
      throw err;
    }
  }

  // BRAND SETTINGS — stored as a single `brand` blob on the API
  async function updateSettings(changes) {
    dispatch({ type: 'UPDATE_SETTINGS', changes });  // optimistic
    try {
      // Merge with existing brand settings before saving so partial updates
      // don't wipe previously-stored fields.
      const current = await settingsApi.get('brand').catch(() => ({}));
      const existing = typeof current?.value === 'object' ? current.value : {};
      await settingsApi.set('brand', { ...existing, ...changes });
    } catch (err) {
      // Revert: re-fetch and flatten the brand blob back into state
      const fresh = await settingsApi.getAll();
      const { brand } = fresh;
      const flatBrand = typeof brand === 'object' && brand !== null ? brand : {};
      dispatch({ type: 'SET_SETTINGS', settings: flatBrand });
      throw err;
    }
  }

  // USERS (admin)
  async function addUser(payload) {
    const user = await usersApi.create(payload);
    dispatch({ type: 'ADD_USER', user });
    logActivity('Created user', user.name);
    return user;
  }

  async function updateUser(id, changes) {
    const user = await usersApi.update(id, changes);
    dispatch({ type: 'UPDATE_USER', user });
    return user;
  }

  // ─────────────────────────────────────────────────────────
  // Legacy dispatch shim
  // Allows older components to still call dispatch({ type, ... })
  // while we migrate them to the async mutators above.
  // ─────────────────────────────────────────────────────────
  function appDispatch(action) {
    switch (action.type) {
      // AI keys
      case 'SET_ANTHROPIC_KEY': setAnthropicKey(action.key);  return;
      case 'SET_OPENAI_KEY':    setOpenaiKey(action.key);     return;

      // Mutations routed to async functions (fire-and-forget)
      case 'ADD_IDEA':           addIdea(action.idea);           return;
      case 'UPDATE_IDEA':        updateIdea(action.id, action.changes); return;
      case 'REMOVE_IDEA':        removeIdea(action.id);          return;
      case 'ADD_CONTENT_PIECES': addContentPieces(action.pieces); return;
      case 'UPDATE_CONTENT_PIECE': updateContentPiece(action.id, action.changes); return;
      case 'REMOVE_CONTENT_PIECE': removeContentPiece(action.id); return;
      case 'SCHEDULE_PIECE':     schedulePiece(action.pieceId, action.dateStr); return;
      case 'UNSCHEDULE_PIECE':   unschedulePiece(action.pieceId, action.dateStr); return;
      case 'ADD_CON_POST':       addConPost(action.post);        return;
      case 'UPDATE_CON_POST':    updateConPost(action.id, action.changes); return;
      case 'REMOVE_CON_POST':    removeConPost(action.id);       return;
      case 'ADD_PARTNER':        addPartner(action.partner);     return;
      case 'UPDATE_PARTNER':     updatePartner(action.id, action.changes); return;
      case 'REMOVE_PARTNER':     removePartner(action.id);       return;
      case 'UPDATE_VOICE':       updateVoice(action.voice);      return;
      case 'UPDATE_SETTINGS':    updateSettings(action.settings); return;
      case 'ADD_USER':           addUser(action.user);           return;
      case 'UPDATE_USER':        updateUser(action.id, action.changes); return;

      // Local-only (logging)
      default: dispatch(action);
    }
  }

  // ─────────────────────────────────────────────────────────
  const value = {
    // Auth
    currentUser,
    authLoading,
    login,
    logout,

    // Data loading
    dataLoading,
    dataError,
    reloadData: loadAllData,

    // Navigation
    activeWorld, setActiveWorld,
    bizTab,  setBizTab,
    conTab,  setConTab,
    activeQueueId, setActiveQueueId,

    // Data
    ideas:         state.ideas,
    contentPieces: state.contentPieces,
    conPosts:      state.conPosts,
    partners:      state.partners,
    scheduled:     state.scheduled,
    voiceState:    state.voiceState,
    settings:      state.settings,
    activityLog:   state.activityLog,
    appUsers:      state.appUsers,

    // Async mutators (preferred — use these in new/updated components)
    addIdea, updateIdea, removeIdea,
    addContentPieces, updateContentPiece, removeContentPiece,
    schedulePiece, unschedulePiece,
    addConPost, updateConPost, removeConPost,
    addPartner, updatePartner, removePartner,
    updateVoice,
    updateSettings,
    addUser, updateUser,

    // Legacy dispatch shim (keeps older components working)
    dispatch: appDispatch,

    // AI keys (client-side only)
    anthropicKey,
    openaiKey,
    anthropicKeyReady,
    openaiKeyReady,
    setAnthropicKey,
    setOpenaiKey,

    // Helpers
    logActivity,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
